import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Eye, ArrowLeft } from 'lucide-react';
import { usePldAlerts, PldAlert, getSeverityLabel, getSeverityColor, getStatusLabel, getRuleTypeLabel } from '../../../hooks/usePld';

const severityBadgeClasses: Record<string, string> = {
  red: 'bg-red-500/20 text-red-400',
  orange: 'bg-orange-500/20 text-orange-400',
  blue: 'bg-blue-500/20 text-blue-400',
  yellow: 'bg-yellow-500/20 text-yellow-400',
  gray: 'bg-surface-600 text-surface-300',
};

const statusBadgeClasses: Record<string, string> = {
  PENDING: 'bg-yellow-500/20 text-yellow-400',
  REVIEWED: 'bg-green-500/20 text-green-400',
  ESCALATED: 'bg-purple-500/20 text-purple-400',
  CLOSED: 'bg-surface-600 text-surface-300',
};

export function PldAlertList() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: '', severity: '', page: 1, limit: 20 });
  const { data, isLoading } = usePldAlerts(filters);

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Alertas PLD</h1>
          <p className="text-surface-400">Lista de alertas de prevenção à lavagem de dinheiro</p>
        </div>
        <Link to="/admin/compliance/pld" className="flex items-center gap-2 text-primary-400 hover:text-primary-300">
          <ArrowLeft className="w-4 h-4" /> Voltar ao Dashboard
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
        <div className="flex flex-wrap gap-4 items-end">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="w-44 px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos os status</option>
            <option value="PENDING">Pendente</option>
            <option value="REVIEWED">Analisado</option>
            <option value="ESCALATED">Escalado</option>
            <option value="CLOSED">Fechado</option>
          </select>
          <select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value, page: 1 })}
            className="w-44 px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todas as severidades</option>
            <option value="CRITICAL">Crítica</option>
            <option value="HIGH">Alta</option>
            <option value="MEDIUM">Média</option>
            <option value="LOW">Baixa</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data?.data?.length ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-surface-600" />
            <p className="text-surface-400">Nenhum alerta encontrado</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Regra</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Usuário</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Severidade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {data.data.map((alert: PldAlert) => (
                <tr key={alert.id} className="hover:bg-surface-800/50 transition-colors">
                  <td className="px-6 py-4 text-sm">{formatDate(alert.createdAt)}</td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-sm">{alert.ruleName}</div>
                    <div className="text-xs text-surface-500">{getRuleTypeLabel(alert.ruleType)}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-surface-300">{alert.userName || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${severityBadgeClasses[getSeverityColor(alert.severity)] || severityBadgeClasses.gray}`}>
                      {getSeverityLabel(alert.severity)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusBadgeClasses[alert.status] || statusBadgeClasses.CLOSED}`}>
                      {getStatusLabel(alert.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => navigate(`/admin/compliance/pld/alerts/${alert.id}`)}
                      className="p-2 text-surface-400 hover:text-primary-400 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default PldAlertList;
